import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BalanceGraph, AmortTable, buildAmortization, MONTHS } from './_finHelpers';

export default function PersonalLoanCalc() {
  const [amount, setAmount] = useState(20000);
  const [rate, setRate] = useState(10);
  const [years, setYears] = useState(5);
  const [months, setMonths] = useState(0);
  const [startMonth, setStartMonth] = useState(new Date().getMonth()+1);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [includeFees, setIncludeFees] = useState(false);
  const [origFee, setOrigFee] = useState(5);
  const [otherFee, setOtherFee] = useState(0);

  const c = useMemo(() => {
    const n = years * 12 + months;
    const feeAmt = includeFees ? amount * origFee/100 + otherFee : 0;
    const financed = amount + (includeFees ? feeAmt : 0);
    const amort = buildAmortization(financed, rate, n, startMonth, startYear);
    const payoff = new Date(startYear, startMonth - 1 + n, 1);
    return { financed, feeAmt, amort, payoff };
  }, [amount, rate, years, months, includeFees, origFee, otherFee, startMonth, startYear]);

  return (
    <>
      <CalcShell look="cnet" title="Personal Loan Calculator" subtitle="Calculate monthly payment, total interest, and payoff schedule for a personal loan."
        about={<>
          <p>A <b>personal loan</b> is an unsecured installment loan with a fixed rate and term, typically 1–7 years. Rates vary widely (6%–36%) based on credit and lender.</p>
        </>}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="field"><label>Loan amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
            <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
            <div className="field"><label>Loan term</label>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
                <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
              </div>
            </div>
            <div className="field"><label>Start date</label>
              <div className="grid grid-cols-2 gap-2">
                <select value={startMonth} onChange={e=>setStartMonth(+e.target.value)}>{MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}</select>
                <input type="number" value={startYear} onChange={e=>setStartYear(+e.target.value||0)}/>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm mt-3 font-semibold"><input type="checkbox" checked={includeFees} onChange={e=>setIncludeFees(e.target.checked)}/> Include fee and insurance</label>
            {includeFees && <>
              <div className="field mt-3"><label>Origination fee (% of principal)</label><input type="number" step="0.01" value={origFee} onChange={e=>setOrigFee(+e.target.value||0)}/></div>
              <div className="field"><label>Other fees ($)</label><input type="number" value={otherFee} onChange={e=>setOtherFee(+e.target.value||0)}/></div>
            </>}
            <button className="mt-4 w-full py-2.5 bg-blue-700 text-white rounded-lg font-semibold text-sm">Compare Personal Loan Rates</button>
          </div>
          <div>
            <div className="result-panel mb-4"><div className="result-title">Monthly pay</div><div className="result-value" data-testid="pl-monthly">{fmt(c.amort.M)}</div></div>
            <table className="data-table mb-4"><tbody>
              <tr><td>Total of {years*12+months} loan payments</td><td className="text-right font-semibold">{fmt(c.amort.totalPaid)}</td></tr>
              <tr><td>Total interest</td><td className="text-right text-orange-600">{fmt(c.amort.totalInterest)}</td></tr>
              <tr><td>Payoff date</td><td className="text-right font-semibold">{MONTHS[c.payoff.getMonth()]}. {c.payoff.getFullYear()}</td></tr>
            </tbody></table>
            <PieChart parts={[{label:'Loan amount', value:c.financed, color:'#2563eb'},{label:'Interest', value:c.amort.totalInterest, color:'#10b981'}]} size={180}/>
          </div>
        </div>
        <StepsPanel steps={[
          `Financed principal = ${fmt(c.financed)}${includeFees?` (incl. ${fmt(c.feeAmt)} fees)`:''}`,
          `Monthly = ${fmt(c.amort.M)} for ${years*12+months} months`,
          `Total interest ≈ <b>${fmt(c.amort.totalInterest)}</b>`,
        ]}/>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div><h3 className="font-bold text-slate-900 mb-3">Amortization Schedule</h3><AmortTable schedule={c.amort}/></div>
          <div><h3 className="font-bold text-slate-900 mb-3">Balance / Interest / Payment</h3><BalanceGraph yearly={c.amort.yearly} startBalance={c.financed}/></div>
        </div>
      </CalcShell>
    </>
  );
}
